import React, { FunctionComponent } from 'react';
import { useTheme } from 'styled-components';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { Field } from '@savgroup-front-common/core/molecules/Field';
import { CheckIcon } from '@savgroup-front-common/core/protons/icons';
import {
  FileSparePartRequestLine,
  REQUEST_LINE_STATUS,
} from '@savgroup-front-common/types';

import { getAdressText, getStatusText } from './DeliveredSpareParts.helpers';
import {
  $DeliveredSparePart,
  $DeliveredSparePartActionButton,
  $DeliveredSparePartActionButtonWrapper,
  $DeliveredSparePartColAlignCenter,
  $DeliveredSparePartColWithPaddingTopForXs,
  $DeliveredSparePartIconAndText,
  $DeliveredSparePartIconAndTextMessage,
} from './DeliveredSpareParts.styles';
import messages from './messages';

export interface DeliveredSparePartProps {
  onChange?: () => void;
  requestLine: FileSparePartRequestLine;
}

const DeliveredSparePart: FunctionComponent<
  React.PropsWithChildren<DeliveredSparePartProps>
> = ({ requestLine, onChange }) => {
  const isDelivered =
    requestLine.requestLineStatus === REQUEST_LINE_STATUS.ORDER_DELIVERED;

  const isDisabled =
    requestLine.requestLineStatus === REQUEST_LINE_STATUS.TO_PROCESS;
  const theme = useTheme();

  return (
    <$DeliveredSparePart>
      <div data-id={requestLine.requestLineId}>
        <$DeliveredSparePartColWithPaddingTopForXs>
          <Field
            label={messages.ref}
            dataTestId={`deliveredSparePart_${requestLine.requestLineId}_sparePartReference`}
          >
            {requestLine.sparePartReference}
          </Field>
        </$DeliveredSparePartColWithPaddingTopForXs>
        <$DeliveredSparePartColWithPaddingTopForXs>
          <Field
            label={messages.supplier}
            dataTestId={`deliveredSparePart_${requestLine.requestLineId}_supplierName`}
          >
            {requestLine.supplierName}
          </Field>
        </$DeliveredSparePartColWithPaddingTopForXs>
        <$DeliveredSparePartColWithPaddingTopForXs>
          <Field
            label={messages.statusKey}
            dataTestId={`deliveredSparePart_${requestLine.requestLineId}_requestLineStatus`}
          >
            <SafeFormattedMessageWithoutSpread
              message={getStatusText(requestLine.requestLineStatus)}
            />
          </Field>
        </$DeliveredSparePartColWithPaddingTopForXs>
        <$DeliveredSparePartColWithPaddingTopForXs>
          <Field
            label={messages.address}
            dataTestId={`deliveredSparePart_${requestLine.requestLineId}_deliveryAddress`}
          >
            {getAdressText(requestLine.deliveryAddress)}
          </Field>
        </$DeliveredSparePartColWithPaddingTopForXs>
      </div>

      <div>
        {!isDelivered && (
          <$DeliveredSparePartActionButtonWrapper>
            <$DeliveredSparePartActionButton
              theme={theme}
              disabled={isDisabled}
              type={BUTTON_TYPES.BUTTON}
              onClick={() => onChange && onChange()}
              dataTestId={`deliveredSparePart_${requestLine.requestLineId}_confirm`}
            >
              <SafeFormattedMessageWithoutSpread message={messages.confirm} />
            </$DeliveredSparePartActionButton>
          </$DeliveredSparePartActionButtonWrapper>
        )}

        {isDelivered && (
          <$DeliveredSparePartColAlignCenter
            theme={theme}
            data-testid={`deliveredSparePart_${requestLine.requestLineId}_submitted`}
          >
            <$DeliveredSparePartIconAndText>
              <CheckIcon color={theme.colors.success} />
              <$DeliveredSparePartIconAndTextMessage>
                <SafeFormattedMessageWithoutSpread
                  message={messages.submitted}
                />
              </$DeliveredSparePartIconAndTextMessage>
            </$DeliveredSparePartIconAndText>
          </$DeliveredSparePartColAlignCenter>
        )}
      </div>
    </$DeliveredSparePart>
  );
};

export default DeliveredSparePart;
